import { fm, weekDays, weekdayShort } from './dates';
import { bin, f0, f1, GREEN, pc } from './format';
import { DASH, REFERENCE_BAR, RAMP_FG, RUST } from './palette';
import { trendWeeks, weekLabel } from './weeks';

/** Why a week has no fibre or water bar yet ('' when it is loaded, or the trend isn't fibre or water). */
function unloaded(tab, x) {
  const status = tab === 'nut' ? x.fibre.status : tab === 'wat' ? x.water.status : 'ready';
  return status === 'ready' ? '' : status === 'loading' ? 'Loading…' : 'Unavailable';
}

// The weekly trend (Wellness Trend's `trend`) and the collapsible by-week
// sections, over up to 12 weeks ending at the selected week. The first logged
// week is the grey reference bar and the dashed line.

export const TREND = {
  sym: { val: (x) => x.sym.avg, fmt: f1, max: 5, color: '#B5654A', ticks: [0, 1, 2, 3, 4, 5],
    title: 'Average symptom severity by week', scope: 'Average of daily check-ins · 0–5 · lower is better' },
  bowel: { val: (x) => x.bowel.normal, fmt: pc, max: 100, color: '#8A6A58', ticks: [0, 25, 50, 75, 100],
    title: 'Stool consistency by week', scope: 'Share of movements in Bristol Type 3–4' },
  nut: { val: (x) => x.fibre.pct, fmt: pc, max: 100, color: '#5E8C5A', ticks: [0, 25, 50, 75, 100],
    title: 'Fibre adherence by week', scope: 'Days fibre target met ÷ days fibre logged' },
  wat: { val: (x) => x.water.pct, fmt: pc, max: 100, color: '#3E8FB0', ticks: [0, 25, 50, 75, 100],
    title: 'Hydration adherence by week', scope: 'Average daily water vs each day’s plan, capped at 100%' },
};

/**
 * @param {'sym'|'bowel'|'nut'|'wat'} tab - The trend to draw ('nut' = fibre, 'wat' = water).
 * @param {string} ws - The selected week's Monday.
 */
export function buildTrend(ctx, tab, ws) {
  const T = TREND[tab];
  const pct = (v) => Math.max(0, Math.min(100, (v / T.max) * 100));
  const first = ctx.firstWeek ? ctx.week(ctx.firstWeek) : null;
  const ref = first && !unloaded(tab, first) ? T.val(first) : null;
  const noTarget = tab === 'nut' && ctx.fibreTarget === null;
  return {
    title: T.title,
    scope: T.scope,
    ticks: T.ticks.map((tk) => ({ b: `${pct(tk)}%`, label: String(tk) })),
    avgB: ref == null ? '0%' : `${pct(ref)}%`,
    avgOp: ref == null ? '0' : '1',
    avgTxt: `First logged week ${T.fmt(ref)}`,
    legend: [
      { w: '12px', h: '12px', bg: T.color, border: 'none', label: 'Selected week' },
      { w: '12px', h: '12px', bg: REFERENCE_BAR, border: 'none', label: 'First logged week' },
      { w: '18px', h: '2px', bg: DASH, border: 'none', label: 'First logged level' },
    ],
    hasNote: noTarget,
    note: noTarget ? 'No fibre target set, so adherence can’t be scored.' : '',
    bars: trendWeeks(ctx, ws).map((w) => {
      const x = ctx.week(w);
      const why = unloaded(tab, x);
      const v = why ? null : T.val(x);
      const h = v == null ? 0 : pct(v);
      return {
        label: weekLabel(ctx, w),
        sub: w === ctx.firstWeek ? `${fm(w)} · First logged` : fm(w),
        vTxt: v == null ? '' : T.fmt(v),
        h: `${h}%`,
        labB: `calc(${h}% + 4px)`,
        bg: w === ws ? T.color : w === ctx.firstWeek ? REFERENCE_BAR : `${T.color}66`,
        border: 'none',
        emptyTxt: why || (v == null ? 'No data' : ''),
      };
    }),
  };
}

/** A heatmap cell (the design's `heatCell`): "—" no check-in, outlined 0, ramp otherwise. */
export function heatCell(v, max, fmt) {
  if (v == null) return { txt: '—', bg: '#fff', fg: '#8a8a8a', ring: 'inset 0 0 0 1px #E3DCD5', tip: 'No check-in that week' };
  if (v === 0) return { txt: '0', bg: '#fff', fg: '#1f1f1f', ring: 'inset 0 0 0 1.5px #CFC4BB', tip: 'Recorded 0' };
  const i = bin(v, max);
  return { txt: fmt(v), bg: RUST[i], fg: RAMP_FG[i], ring: 'none', tip: `${fmt(v)}/5` };
}

/**
 * The tab's collapsible sections (DetailSection's `d`, without `isOpen`).
 * Symptoms: "Symptoms by week" heatmap. Nutrition: "Daily intake" table.
 */
export function buildDetails(ctx, tab, ws, sel) {
  if (tab === 'sym') {
    const weeks = trendWeeks(ctx, ws);
    return [{
      id: 'heat',
      title: 'Symptoms by week',
      desc: 'Average of each gut symptom per week, over check-in days (not logged = 0)',
      defaultOpen: true,
      isBlock: true,
      block: {
        type: 'heat',
        corner: 'SYMPTOM',
        cols: weeks.map((w) => `${weekLabel(ctx, w)}\n${fm(w)}`),
        rows: ctx.names.map((name) => ({ name, weight: 500, cells: weeks.map((w) => heatCell(ctx.week(w).sym.sub[name], 5, f1)) })),
        foot: '“—” = no check-in that week. 0 = check-ins that week, never above 0.',
      },
      tf: { show: false },
    }];
  }
  if (tab === 'nut') {
    const T = ctx.fibreTarget;
    return [{
      id: 'days',
      title: `Daily intake · week of ${fm(ws)}`,
      desc: 'Fibre and water logged each day of the selected week',
      defaultOpen: true,
      isBlock: true,
      block: {
        type: 'table',
        colsCss: '160px repeat(2,minmax(0,1fr))',
        head: ['DAY', 'FIBRE', 'WATER'],
        foot: `“Not logged” = no tracking that day, never 0. MET = at or above ${T ? `the ${f0(T)} g fibre target` : 'target (no fibre target set)'}, or that day’s water plan.`,
        rows: weekDays(ws).filter((d) => d <= ctx.today).map((d) => {
          const status = ctx.fibreStatus(d);
          const g = status === 'ready' ? ctx.fibre[d] : null;
          const met = T && g != null && g >= T;
          const wStatus = ctx.waterStatus(d);
          const w = wStatus === 'ready' ? ctx.water[d] : null;
          const wMet = w?.plan && w.ml >= w.plan;
          return {
            key: d,
            bg: d === sel ? '#FDF7F3' : '#fff',
            cells: [
              { txt: `${weekdayShort(d)}, ${fm(d)}`, fg: '#1f1f1f', weight: d === sel ? 700 : 500, tag: '', tagBg: 'transparent' },
              {
                txt: status === 'loading' ? 'Loading…' : status === 'error' ? 'Unavailable' : g == null ? 'Not logged' : T ? `${f0(g)} / ${f0(T)} g` : `${f0(g)} g`,
                fg: g == null ? '#9a9a9a' : '#1f1f1f',
                weight: 400,
                tag: met ? 'MET' : '',
                tagBg: met ? '#E3F0E7' : 'transparent',
                tagFg: GREEN,
              },
              {
                txt: wStatus === 'loading' ? 'Loading…' : wStatus === 'error' ? 'Unavailable' : !w ? 'Not logged' : w.plan ? `${f0(w.ml)} / ${f0(w.plan)} ml` : `${f0(w.ml)} ml`,
                fg: !w ? '#9a9a9a' : '#1f1f1f',
                weight: 400,
                tag: wMet ? 'MET' : '',
                tagBg: wMet ? '#E1EEF4' : 'transparent',
                tagFg: '#2F7C9C',
              },
            ],
          };
        }),
      },
      tf: { show: false },
    }];
  }
  return [];
}
